import { useEffect, useState } from 'react'
import { searchOrg } from '../services/actions/orgActions'
import { ORG_SEARCH_MIN_LENGTH, ORG_SEARCH_MAX_LENGTH } from '../services/utils/orgSearch'

export default function useOrgSearch(orgSlug, query, { enabled = true, type = 'all', page = 1, limit = 5, delay = 250 } = {}) {
	const term = query.trim()
	const eligible = Boolean(enabled && orgSlug && term.length >= ORG_SEARCH_MIN_LENGTH && term.length <= ORG_SEARCH_MAX_LENGTH)
	const key = JSON.stringify([orgSlug, term, type, page, limit])
	const [state, setState] = useState({ key: null, data: null, error: '', loading: false })
	const [version, setVersion] = useState(0)

	useEffect(() => {
		if (!eligible) {
			setState({ key: null, data: null, error: '', loading: false })
			return undefined
		}
		let disposed = false
		const controller = new AbortController()
		let timeoutId
		setState({ key, data: null, error: '', loading: true })
		const debounceId = setTimeout(async () => {
			timeoutId = setTimeout(() => controller.abort(), 12_000)
			try {
				const data = await searchOrg(orgSlug, { query: term, type, page, limit, signal: controller.signal })
				if (!disposed) setState({ key, data, error: '', loading: false })
			} catch (error) {
				const message = error?.status === 429 || (error?.status === 503 && error?.code === 'ORG_SEARCH_TIMEOUT')
					? error.message
					: 'Search is unavailable. Please try again.'
				if (!disposed) setState({ key, data: null, error: message, loading: false })
			} finally {
				clearTimeout(timeoutId)
			}
		}, delay)
		return () => {
			disposed = true
			clearTimeout(debounceId)
			clearTimeout(timeoutId)
			controller.abort()
		}
	}, [eligible, key, orgSlug, term, type, page, limit, delay, version])

	return {
		data: eligible && state.key === key ? state.data : null,
		error: eligible && state.key === key ? state.error : '',
		loading: eligible && (state.key !== key || state.loading),
		retry: () => setVersion((value) => value + 1),
	}
}
